import { useCallback, useEffect, useState } from "react";
import { UploadCloud } from "lucide-react";
import { adminApi, errText } from "./adminApi";
import { HwQuestions, HwSyllabus } from "./AdminHomework";

const HW = "/hw";
const PUBLISH_CATS = [["class_11", "NEET Class 11"], ["class_12", "NEET Class 12"], ["jee_main", "JEE Main"], ["jee_advanced", "JEE Advanced"]];
const DIFFS = {
  class_11: ["Easy", "Medium", "Hard"], class_12: ["Easy", "Medium", "Hard"],
  jee_main: ["Easy", "Medium", "Hard", "JEE Main"], jee_advanced: ["Medium", "Hard", "JEE Advanced"],
};
const CAT_LABEL = Object.fromEntries(PUBLISH_CATS);

/* PDF → instant publish: upload a question paper, AI extracts every MCQ, and the clean
   ones (with answers, high confidence) go LIVE in student tests immediately. Anything
   uncertain still lands in Question Bank → pending review. AI generation tabs removed
   per owner — the bank grows from real papers only. */
function useSyllabusMap(category) {
  const [chapters, setChapters] = useState([]);
  useEffect(() => {
    if (!category) { setChapters([]); return undefined; }
    let alive = true;
    adminApi.get(`${HW}/admin/syllabus`, { params: { category } }).then(({ data }) => {
      if (!alive) return;
      const map = {};
      (data.syllabus || []).forEach((s) => { (map[s.chapter] = map[s.chapter] || []).push(s.topic); });
      setChapters(Object.entries(map).map(([chapter, topics]) => ({ chapter, topics })));
    }).catch(() => { if (alive) setChapters([]); });
    return () => { alive = false; };
  }, [category]);
  return chapters;
}

function PdfPublish() {
  const [sel, setSel] = useState({ category: "class_11", chapter: "", topic: "" });
  const [subtopic, setSubtopic] = useState("");
  const [difficulty, setDifficulty] = useState("Medium");
  const [files, setFiles] = useState([]);
  const [answerFile, setAnswerFile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const chapters = useSyllabusMap(sel.category);
  const topics = chapters.find((c) => c.chapter === sel.chapter)?.topics || [];

  const load = useCallback(() => adminApi.get(`${HW}/admin/uploads`)
    .then(({ data }) => setJobs((data.jobs || []).filter((j) => j.auto_publish))).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!jobs.some((j) => j.status === "processing")) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [jobs, load]);

  const publish = async () => {
    if (!files.length || !sel.chapter || !sel.topic) { setError("Pick chapter + topic and at least one PDF."); return; }
    setBusy(true); setError(""); setNotice("");
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      form.append("category", sel.category);
      form.append("chapter", sel.chapter);
      form.append("topic", sel.topic);
      form.append("subtopic", subtopic);
      form.append("difficulty", difficulty);
      form.append("qtype", "mcq");
      form.append("auto_publish", "true");
      if (answerFile) form.append("answer_files", answerFile);
      const { data } = await adminApi.post(`${HW}/admin/uploads`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setNotice(`${data.jobs.length} PDF(s) being extracted — clean MCQs publish themselves. Watch the jobs below.${data.rejected.length ? ` Rejected: ${data.rejected.join("; ")}` : ""}`);
      setFiles([]);
      load();
    } catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="pdf-publish">
      <div className="admin-card">
        <h4 style={{ marginTop: 0 }}>Upload paper → MCQs live in minutes</h4>
        <p className="lead-sub" style={{ fontSize: 11 }}>
          Pick the target, attach the PDF (e.g. 45 questions), and extract. Clean MCQs with answers publish
          straight into student tests — no review step. Uncertain or answer-less questions wait in the Question Bank tab instead.</p>
        <div className="admin-row" style={{ flexWrap: "wrap", gap: 10, marginTop: 10 }}>
          <select className="admin-input" value={sel.category}
                  onChange={(e) => { setSel({ category: e.target.value, chapter: "", topic: "" }); setDifficulty(DIFFS[e.target.value][0]); }}
                  data-testid="pp-category">
            {PUBLISH_CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select className="admin-input" value={sel.chapter} onChange={(e) => setSel((s) => ({ ...s, chapter: e.target.value, topic: "" }))} data-testid="pp-chapter">
            <option value="">Chapter…</option>
            {chapters.map((c) => <option key={c.chapter}>{c.chapter}</option>)}
          </select>
          <select className="admin-input" value={sel.topic} onChange={(e) => setSel((s) => ({ ...s, topic: e.target.value }))} data-testid="pp-topic">
            <option value="">Topic…</option>
            {topics.map((t) => <option key={t}>{t}</option>)}
          </select>
          <select className="admin-input" value={difficulty} onChange={(e) => setDifficulty(e.target.value)} data-testid="pp-difficulty">
            {(DIFFS[sel.category] || []).map((d) => <option key={d}>{d}</option>)}
          </select>
          <input className="admin-input" style={{ maxWidth: 170 }} placeholder="Subtopic (optional)" value={subtopic}
                 onChange={(e) => setSubtopic(e.target.value)} data-testid="pp-subtopic" />
        </div>
        <div className="admin-row" style={{ marginTop: 10, flexWrap: "wrap", gap: 10 }}>
          <input type="file" accept="application/pdf" multiple onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 10))}
                 data-testid="pp-files" style={{ color: "#9db1c9", fontSize: 13 }} />
          <label style={{ color: "#9db1c9", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
            Answer key / solutions PDF (optional — fills answers + solutions):
            <input type="file" accept="application/pdf" onChange={(e) => setAnswerFile((e.target.files || [])[0] || null)}
                   data-testid="pp-answer-file" style={{ color: "#9db1c9", fontSize: 13 }} />
          </label>
          <button className="admin-btn" disabled={busy || !files.length} onClick={publish} data-testid="pp-submit">
            <UploadCloud size={14} /> {busy ? "Uploading…" : `Extract & publish${files.length ? ` ${files.length} PDF${files.length === 1 ? "" : "s"}` : ""}`}
          </button>
        </div>
        {error && <p className="admin-error" data-testid="pp-error">{error}</p>}
        {notice && <p className="lead-sub" style={{ color: "#4ade80" }} data-testid="pp-notice">{notice}</p>}
      </div>
      {jobs.map((j, i) => (
        <div className="admin-card" key={j.job_id} data-testid={`pp-job-${i}`}>
          <div className="admin-row" style={{ flexWrap: "wrap" }}>
            <span className={`status-pill ${j.status === "ready" ? "approved" : j.status === "failed" ? "failed" : "review"}`} data-testid={`pp-job-status-${i}`}>
              {j.status === "processing" ? (j.progress || "EXTRACTING…") : j.status.toUpperCase()}</span>
            <b style={{ color: "#eef4fc", flex: 1 }}>{j.filename}</b>
            <span className="lead-sub">{CAT_LABEL[j.category] || j.category} · {j.chapter} · {j.topic}</span>
          </div>
          {j.status === "ready" && j.stats && (
            <p className="lead-sub" style={{ marginTop: 6 }} data-testid={`pp-job-stats-${i}`}>
              <b style={{ color: "#4ade80" }}>{j.stats.published || 0} MCQs live in student tests</b>
              {j.stats.answers_matched > 0 && <span> · answers matched {j.stats.answers_matched}</span>}
              {(j.stats.awaiting_review || 0) > 0 && <span> · {j.stats.awaiting_review} awaiting review (Question Bank tab)</span>}
              <span> · duplicates {j.stats.duplicates} · errors {j.stats.errors}</span>
            </p>)}
          {j.status === "failed" && <p className="admin-error" style={{ marginTop: 6 }}>{j.error}</p>}
        </div>
      ))}
      {jobs.length === 0 && <div className="admin-empty">No instant-publish uploads yet.</div>}
    </div>
  );
}

/* Test Bank — everything that curates the question bank powering the student
   Test Series module. Kept fully separate from the document-based Homework Review Desk. */
const TABS = [
  ["publish", "PDF → Publish (Instant)"], ["questions", "Question Bank"], ["syllabus", "Syllabus"],
];

export default function AdminTestBank() {
  const [tab, setTab] = useState("publish");
  return (
    <div data-testid="admin-testbank">
      <h4 style={{ marginBottom: 4 }}>Test Bank — powers the student Test Series</h4>
      <span className="lead-sub">Upload a question-paper PDF: AI extracts every MCQ and the clean ones go live in student tests within minutes — for NEET Class 11, NEET Class 12 and JEE. (Slow AI bank-filling has been removed.)</span>
      <div className="admin-tabs" style={{ marginTop: 12, flexWrap: "wrap" }} data-testid="tb-admin-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} className={tab === k ? "active" : ""} onClick={() => setTab(k)} data-testid={`tb-tab-${k}`}>{label}</button>
        ))}
      </div>
      {tab === "publish" && <PdfPublish />}
      {tab === "questions" && <HwQuestions />}
      {tab === "syllabus" && <HwSyllabus />}
    </div>
  );
}
